import { useNavigate } from 'react-router-dom';
import { LogOut, Sun, Moon, Menu, Boxes, MonitorDot, Rocket, type LucideIcon } from 'lucide-react';
import { useAuthStore } from '@/stores/auth.store';
import { useThemeStore } from '@/stores/theme.store';
import { useSpaceStore, type PanelSpace } from '@/stores/space.store';
import { GameSwitcher } from './GameSwitcher';
import styles from './Header.module.scss';

const SPACES: { id: PanelSpace; label: string; icon: LucideIcon; path: string }[] = [
  { id: 'supervision', label: 'Supervision', icon: MonitorDot, path: '/dashboard' },
  { id: 'configuration', label: 'Configuration', icon: Boxes, path: '/config' },
  { id: 'deploiement', label: 'Déploiement', icon: Rocket, path: '/deploiement' },
];

export function Header({ onMenuClick }: { onMenuClick?: () => void }) {
  const { user, logout } = useAuthStore();
  const resolved = useThemeStore((s) => s.resolved);
  const toggleTheme = useThemeStore((s) => s.toggle);
  const space = useSpaceStore((s) => s.space);
  const setSpace = useSpaceStore((s) => s.setSpace);
  const navigate = useNavigate();

  const goToSpace = (target: (typeof SPACES)[number]) => {
    if (target.id === space) return;
    setSpace(target.id);
    navigate(target.path);
  };

  return (
    <header className={styles.header}>
      <div className={styles.left}>
        <button className={styles.menuButton} onClick={onMenuClick} aria-label="Menu">
          <Menu size={22} strokeWidth={2} />
        </button>
        <GameSwitcher />
        <div className={styles.spaceSwitch} data-space={space} role="tablist" aria-label="Espace du panel">
          <div className={styles.spaceSwitchThumb} />
          {SPACES.map((s) => {
            const Icon = s.icon;
            return (
              <button
                key={s.id}
                type="button"
                role="tab"
                aria-selected={space === s.id}
                className={`${styles.spaceSwitchItem} ${space === s.id ? styles.spaceSwitchItemActive : ''}`}
                onClick={() => goToSpace(s)}
              >
                <Icon size={15} strokeWidth={2} />
                <span className={styles.spaceSwitchLabel}>{s.label}</span>
              </button>
            );
          })}
        </div>
      </div>

      <div className={styles.right}>
        <button
          className={styles.themeToggle}
          onClick={toggleTheme}
          title={resolved === 'dark' ? 'Passer en mode clair' : 'Passer en mode sombre'}
          aria-label="Changer de thème"
        >
          {resolved === 'dark' ? <Sun size={18} strokeWidth={2} /> : <Moon size={18} strokeWidth={2} />}
        </button>

        {user && (
          <div className={styles.userMenu}>
            <span className={styles.gamertag}>{user.gamertag}</span>
            <button className={styles.logoutBtn} onClick={logout}>
              <LogOut size={16} strokeWidth={2} />
              <span>Déconnexion</span>
            </button>
          </div>
        )}
      </div>
    </header>
  );
}
